import { shapeLeadProviderApi } from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { toLoggableError } from 'utils/axios';
import { logger } from 'utils/logger';
import { createNamedActions } from 'utils/namedActionTypesProxy';

export const SHAPE_LEAD_PROVIDER_ACTION_TYPE = createNamedActions('shapeLeadProvider', {
  GET_TOKENS_REQUEST: 'GET_TOKENS_REQUEST',
  GET_TOKENS_SUCCESS: 'GET_TOKENS_SUCCESS',
  GET_TOKENS_FAILURE: 'GET_TOKENS_FAILURE',

  GET_INBOUND_LEADS_REQUEST: 'GET_INBOUND_LEADS_REQUEST',
  GET_INBOUND_LEADS_SUCCESS: 'GET_INBOUND_LEADS_SUCCESS',
  GET_INBOUND_LEADS_FAILURE: 'GET_INBOUND_LEADS_FAILURE',

  GET_AGGREGATES_REQUEST: 'GET_AGGREGATES_REQUEST',
  GET_AGGREGATES_SUCCESS: 'GET_AGGREGATES_SUCCESS',
  GET_AGGREGATES_FAILURE: 'GET_AGGREGATES_FAILURE',
});

export const getShapeTokensAction = catchAsyncActionError(() => async dispatch => {
  dispatch({ type: SHAPE_LEAD_PROVIDER_ACTION_TYPE.GET_TOKENS_REQUEST });

  const { data } = await shapeLeadProviderApi.getTokens();

  dispatch({
    type: SHAPE_LEAD_PROVIDER_ACTION_TYPE.GET_TOKENS_SUCCESS,
    payload: data ?? [],
  });

  return data;
});

// The plaintext token comes back once, on mint, and is never re-fetchable — the caller shows it in
// the reveal-once modal and stores nothing sensitive. We only refresh the (metadata-only) token list.
//
// The read-back must be TRULY best-effort, hence the explicit `.catch`: `silent` only suppresses
// catchAsyncActionError's toast — it still `return Promise.reject(err)` unconditionally. Letting the
// refresh reject would reject the MINT itself, so a failed `GET /tokens` would destroy the
// once-only plaintext token before the caller could reveal it, while a live token sat on the
// provider. The write's outcome must be reported on its own merits.
const refreshTokensBestEffort = dispatch =>
  dispatch(getShapeTokensAction({ silent: true })).catch(error =>
    logger.error('Failed to refresh Shape tokens', toLoggableError(error)),
  );

export const mintShapeTokenAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await shapeLeadProviderApi.mintToken();

  await refreshTokensBestEffort(dispatch);

  return data;
});

// Same contract: the token IS revoked once the POST resolves — a failing list refresh must not
// report that as a failed revoke.
export const revokeShapeTokenAction = catchAsyncActionError(({ tokenId }) => async dispatch => {
  await shapeLeadProviderApi.revokeToken(tokenId);

  await refreshTokensBestEffort(dispatch);
});

export const getShapeInboundLeadsAction = catchAsyncActionError((params = {}) => async dispatch => {
  dispatch({ type: SHAPE_LEAD_PROVIDER_ACTION_TYPE.GET_INBOUND_LEADS_REQUEST });

  const { data } = await shapeLeadProviderApi.getInboundLeads(params);

  dispatch({
    type: SHAPE_LEAD_PROVIDER_ACTION_TYPE.GET_INBOUND_LEADS_SUCCESS,
    payload: data ?? {},
  });

  return data;
});

export const getShapeInboundLeadAction = catchAsyncActionError(({ id }) => async () => {
  const { data } = await shapeLeadProviderApi.getInboundLead(id);

  return data;
});

export const replayShapeInboundLeadAction = catchAsyncActionError(({ id }) => async () => {
  const { data } = await shapeLeadProviderApi.replayInboundLead(id);

  return data;
});

export const getShapeAggregatesAction = catchAsyncActionError((params = {}) => async dispatch => {
  dispatch({ type: SHAPE_LEAD_PROVIDER_ACTION_TYPE.GET_AGGREGATES_REQUEST });

  const { data } = await shapeLeadProviderApi.getAggregates(params);

  dispatch({
    type: SHAPE_LEAD_PROVIDER_ACTION_TYPE.GET_AGGREGATES_SUCCESS,
    payload: data ?? null,
  });

  return data;
});
